/**
 * The six artifacts of the "Product update" demo: a two-week cycle told to the whole company.
 *
 * Written for the people who never open the tracker. Each page makes one point in a short headline and
 * lets a piece of the product carry it: the calendar that launched, the smaller wins as three cards, the
 * invoice with the wrong cent, the usage line, and the next three dates.
 *
 * Dressed in Linear's dark system: a near black ground, raised panels with a 1px translucent border,
 * status icons in place of pills, mono uppercase labels, and the two-tone headline that states the point
 * in white and carries on in grey.
 */

import { DISPLAY, MONO, circle, frame, line, lines, path, rect, text, wrap, type DemoSlide, type LinesOpts } from './slideSvg';

const C = {
  ground: '#08090A',
  panel: '#0F1011',
  raised: '#141516',
  raised3: '#191A1B',
  fill: '#1C1C1F',
  border: '#23252A',
  border2: '#34343A',
  /** The translucent edge every panel wears. */
  edge: 'rgba(255,255,255,0.08)',
  hair: 'rgba(255,255,255,0.06)',
  text: '#F7F8F8',
  text2: '#D0D6E0',
  text3: '#8A8F98',
  text4: '#62666D',
  indigo: '#5E6AD2',
  accent: '#7170FF',
  green: '#27A644',
  yellow: '#F0BF00',
  orange: '#FC7840',
  teal: '#00B8CC',
} as const;

/** A colour at an opacity, for the tinted fills. */
function tint(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
}

const W = 1280;
const H = 720;
/** Side margin; the grid runs from M to R. */
const M = 64;
const R = W - M;
/** Every visual sits between these two lines. */
const TOP = 216;
const BOTTOM = 656;

/* ── type ─────────────────────────────────────────────────────────────────────────────────── */

/** Advance width of a string in Inter, close enough to size a pill around it. */
function measure(str: string, size: number): number {
  let em = 0;
  for (const ch of str) {
    if (" ilIj.,:;!|·'".includes(ch)) em += 0.26;
    else if ('ftr()[]/1'.includes(ch)) em += 0.37;
    else if ('mwM%@'.includes(ch)) em += 0.88;
    else if (ch === 'W') em += 1;
    else if (ch >= 'A' && ch <= 'Z') em += 0.68;
    else if (ch >= '0' && ch <= '9') em += 0.63;
    else em += 0.59;
  }
  return em * size;
}

/** UI text: Inter at Linear's small-size tracking, white unless told otherwise. */
const ui = (x: number, y: number, size: number, str: string, opts: LinesOpts = {}) =>
  text(x, y, size, str, { fill: C.text, tracking: '-0.003em', ...opts });

/** A label: mono, uppercase, quiet. */
const label = (x: number, y: number, str: string, fill: string = C.text3, anchor: LinesOpts['anchor'] = 'start', size = 12) =>
  text(x, y, size, str.toUpperCase(), { font: MONO, fill, anchor, tracking: '0.02em' });

/** The running line, in the same place on every content slide. */
const runner = (page: number) => label(M, 48, 'Tempo, cycle 42', C.text4) + label(R, 48, String(page).padStart(2, '0'), C.text4, 'end');

const HEAD = { size: 44, lead: 48, y: 120 };
const headOpts = { font: DISPLAY, weight: 500, tracking: '-0.022em' };

/**
 * The headline, in the same place on every content slide. With a continuation it is Linear's two-tone
 * statement: the point in white, the rest of the sentence in grey after it, wrapping onto the next line.
 * The first line is laid out whole in grey and the point drawn again over its start in white, with the
 * grey masked away beneath it, so the two tones meet exactly where the text engine puts them.
 */
function headline(slide: number, point: string, rest: string[] = []): string {
  const white = (fill: string) => text(M, HEAD.y, HEAD.size, point, { ...headOpts, fill });
  if (!rest.length) return white(C.text);
  const id = `update-${slide}-tone`;
  return (
    `<defs><mask id="${id}" maskUnits="userSpaceOnUse" x="0" y="0" width="${W}" height="${H}">` +
    rect(0, 0, W, H, '#FFFFFF') +
    `<g stroke="#000000" stroke-width="3" stroke-linejoin="round">${white('#000000')}</g></mask></defs>` +
    `<g mask="url(#${id})">${lines(M, HEAD.y, HEAD.size, HEAD.lead, [`${point} ${rest[0]}`, ...rest.slice(1)], { ...headOpts, fill: C.text3 })}</g>` +
    white(C.text)
  );
}

/* ── surfaces ─────────────────────────────────────────────────────────────────────────────── */

/** A raised panel: its fill, a 1px translucent edge, and a faint highlight along the top. */
const panel = (x: number, y: number, w: number, h: number, r = 12, fill: string = C.panel) =>
  rect(x, y, w, h, fill, r) + frame(x, y, w, h, C.edge, r) + line(x + r, y + 1.5, x + w - r, y + 1.5, 'rgba(255,255,255,0.05)');

/** A control or row inside a panel: one level up, with a crisp 1px border. */
const box = (x: number, y: number, w: number, h: number, r = 8, fill: string = C.raised) =>
  rect(x, y, w, h, fill, r) + frame(x, y, w, h, C.border, r);

const ring = (cx: number, cy: number, r: number, stroke: string, width = 1.5, dash?: string) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${stroke}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`;

const strokePath = (d: string, stroke: string, width = 1.5) => path(d, 'none', stroke, width);

interface PillOpts {
  fill?: string;
  stroke?: string;
  color?: string;
  size?: number;
  h?: number;
  pad?: number;
  /** A coloured dot before the label. */
  dot?: string;
  /** An icon before the label, drawn centred on the point it is given. */
  icon?: (cx: number, cy: number) => string;
  /** When set, `x` is the pill's right edge. */
  end?: boolean;
}

const pillWidth = (label: string, o: PillOpts) =>
  Math.round(measure(label, o.size ?? 13) + (o.pad ?? 10) * 2 + (o.icon ? 22 : o.dot ? 14 : 0));

/** Linear's label pill: fully rounded, a 1px border, the label in secondary grey. */
function pill(x: number, cy: number, label: string, o: PillOpts = {}): string {
  const size = o.size ?? 13;
  const h = o.h ?? 26;
  const pad = o.pad ?? 10;
  const w = pillWidth(label, o);
  const left = o.end ? x - w : x;
  const lead = o.icon ? 22 : o.dot ? 14 : 0;
  return (
    rect(left, cy - h / 2, w, h, o.fill ?? C.panel, h / 2) +
    frame(left, cy - h / 2, w, h, o.stroke ?? C.border, h / 2) +
    (o.dot ? circle(left + pad + 4, cy, 3.5, o.dot) : '') +
    (o.icon ? o.icon(left + pad + 8, cy) : '') +
    ui(left + pad + lead, cy + size * 0.36, size, label, { fill: o.color ?? C.text2, weight: 500 })
  );
}

/* ── icons ────────────────────────────────────────────────────────────────────────────────── */

/** Linear's status icons, centred on (cx, cy). */
const status = {
  /** Done: a filled green circle with the check cut out of it. */
  done: (cx: number, cy: number, r = 7) =>
    circle(cx, cy, r, C.green) + strokePath(`M ${cx - r * 0.43} ${cy + r * 0.02} l ${r * 0.3} ${r * 0.3} l ${r * 0.58} ${-r * 0.6}`, C.ground, Math.max(1.3, r * 0.22)),
  /** In progress: a ring with its right half filled. */
  progress: (cx: number, cy: number, r = 7) =>
    ring(cx, cy, r - 0.75, C.yellow) + path(`M ${cx} ${cy - r * 0.45} A ${r * 0.45} ${r * 0.45} 0 0 1 ${cx} ${cy + r * 0.45} Z`, C.yellow),
  /** Backlog, and anything not yet real: a dashed ring. */
  backlog: (cx: number, cy: number, r = 7) => ring(cx, cy, r - 0.75, C.text3, 1.5, '1.6 2.1'),
};

/** Line icons on a 16px box centred on (cx, cy), in Linear's 1.5px stroke. */
const icons = {
  calendar: (cx: number, cy: number, c: string = C.text3) =>
    strokePath(
      `M ${cx - 6} ${cy - 5} h 12 a 1.5 1.5 0 0 1 1.5 1.5 v 9.5 a 1.5 1.5 0 0 1 -1.5 1.5 h -12 a 1.5 1.5 0 0 1 -1.5 -1.5 v -9.5 a 1.5 1.5 0 0 1 1.5 -1.5 Z ` +
        `M ${cx - 7.5} ${cy - 1} h 15 M ${cx - 3.5} ${cy - 7.5} v 3.5 M ${cx + 3.5} ${cy - 7.5} v 3.5`,
      c,
    ),
  search: (cx: number, cy: number, c: string = C.text3) =>
    ring(cx - 1.5, cy - 1.5, 5.5, c) + strokePath(`M ${cx + 2.5} ${cy + 2.5} L ${cx + 6.5} ${cy + 6.5}`, c),
  receipt: (cx: number, cy: number, c: string = C.text3) =>
    strokePath(
      `M ${cx - 5.5} ${cy - 7.5} h 11 v 15 l -2.75 -1.8 l -2.75 1.8 l -2.75 -1.8 l -2.75 1.8 Z ` +
        `M ${cx - 2.5} ${cy - 3.5} h 5 M ${cx - 2.5} ${cy} h 5`,
      c,
    ),
  bubble: (cx: number, cy: number, c: string = C.text3) =>
    strokePath(
      `M ${cx - 5.5} ${cy - 6} h 11 a 2 2 0 0 1 2 2 v 6.5 a 2 2 0 0 1 -2 2 h -5.5 l -3.5 3 v -3 h -2 a 2 2 0 0 1 -2 -2 v -6.5 a 2 2 0 0 1 2 -2 Z`,
      c,
    ),
  chevronLeft: (cx: number, cy: number, c: string = C.text3) => strokePath(`M ${cx + 2} ${cy - 5} L ${cx - 3} ${cy} L ${cx + 2} ${cy + 5}`, c),
  chevronRight: (cx: number, cy: number, c: string = C.text3) => strokePath(`M ${cx - 2} ${cy - 5} L ${cx + 3} ${cy} L ${cx - 2} ${cy + 5}`, c),
  link: (cx: number, cy: number, c: string = C.text3) =>
    strokePath(
      `M ${cx - 1} ${cy + 3.5} l -1.5 1.5 a 3 3 0 0 1 -4.2 -4.2 l 2.6 -2.6 a 3 3 0 0 1 4.2 0 ` +
        `M ${cx + 1} ${cy - 3.5} l 1.5 -1.5 a 3 3 0 0 1 4.2 4.2 l -2.6 2.6 a 3 3 0 0 1 -4.2 0`,
      c,
    ),
};

/** A tiny round avatar: the initial in a colour, on a tint of it. */
const avatar = (cx: number, cy: number, r: number, initial: string, color: string) =>
  circle(cx, cy, r, tint(color, 0.18)) + ui(cx, cy + 4.3, 12, initial, { fill: color, weight: 600, anchor: 'middle' });

/* ── 1 · Cover ────────────────────────────────────────────────────────────────────────────── */

/** The company's mark and name. */
const logo = (x: number, y: number) =>
  rect(x, y, 28, 28, C.text, 7) +
  strokePath(`M ${x + 8} ${y + 9.5} H ${x + 20} M ${x + 14} ${y + 9.5} V ${y + 20.5}`, C.ground, 2.8) +
  ui(x + 40, y + 20, 17, 'Tempo', { weight: 600, tracking: '-0.011em' });

const CHANGES = [
  { state: 'done', label: 'Shipped', title: 'Shared calendars', icon: icons.calendar },
  { state: 'done', label: 'Shipped', title: 'Faster search', icon: icons.search },
  { state: 'progress', label: 'Moved', title: 'Invoices', icon: icons.receipt },
] as const;

/** Linear's changelog line: a hairline, each change's status sitting on it, the change below. */
function changelog(y: number): string {
  const col = (R - M) / 3;
  const node = (i: number) => M + i * col + 9;
  let out = '';
  CHANGES.forEach((change, i) => {
    const x = M + i * col;
    out += line(node(i) + 16, y, i < CHANGES.length - 1 ? node(i + 1) - 16 : R, y, C.edge);
    out += status[change.state](node(i), y, 8);
    out += label(x, y + 44, change.label);
    out += change.icon(x + 8, y + 76) + ui(x + 28, y + 82, 18, change.title, { weight: 500, tracking: '-0.011em' });
  });
  return out;
}

/** The one flourish Linear allows itself: a barely visible glow, here behind the title. */
const glow =
  `<defs><radialGradient id="update-1-glow" gradientUnits="userSpaceOnUse" cx="360" cy="300" r="640">` +
  `<stop offset="0" stop-color="#FFFFFF" stop-opacity="0.045"/><stop offset="1" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs>` +
  rect(0, 0, W, H, 'url(#update-1-glow)');

const cover = wrap(
  rect(0, 0, W, H, C.ground) +
    glow +
    logo(M, 56) +
    text(M, 330, 104, 'Cycle 42', { font: DISPLAY, weight: 500, tracking: '-0.03em', fill: C.text }) +
    text(M, 382, 26, 'What changed for customers', { ...headOpts, fill: C.text3 }) +
    label(M, 422, 'September 8 to 21', C.text3, 'start', 13) +
    changelog(528),
);

/* ── 2 · The big one ──────────────────────────────────────────────────────────────────────── */

const KINDS = {
  room: { color: C.accent, label: 'Rooms' },
  person: { color: C.teal, label: 'People' },
  van: { color: C.orange, label: 'Vans' },
} as const;
type Kind = keyof typeof KINDS;

/** A week of bookings: day (0 is Monday), start and end hour, what is booked, the title. */
const WEEK: [number, number, number, Kind, string][] = [
  [0, 9, 9.5, 'room', 'Room A · Standup'],
  [0, 10, 12, 'van', 'Van 2 · Delivery run'],
  [0, 14, 15, 'person', 'Nino · 1:1'],
  [1, 9, 9.5, 'room', 'Room A · Standup'],
  [1, 11, 12, 'person', 'Luka · Onboarding'],
  [1, 13.5, 16, 'van', 'Van 1 · Site visit'],
  [2, 9, 9.5, 'room', 'Room A · Standup'],
  [2, 10, 11.5, 'room', 'Room B · Design review'],
  [2, 14, 15, 'person', 'Nino · 1:1'],
  [2, 16, 17, 'van', 'Van 2 · Pickup'],
  [3, 9, 9.5, 'room', 'Room A · Standup'],
  [3, 10.5, 12.5, 'van', 'Van 2 · Delivery run'],
  [3, 14, 15.5, 'room', 'Room C · Customer demo'],
  [4, 9, 9.5, 'room', 'Room A · Standup'],
  [4, 11, 12, 'person', 'Ana · Interview'],
  [4, 15, 16, 'room', 'Room B · Retro'],
];

const clock = (hr: number) => `${Math.floor(hr)}:${hr % 1 ? '30' : '00'}`;

/** The shared calendar: one week, rooms, people and vans side by side. */
function weekCalendar(x: number, y: number, w: number, h: number): string {
  const bar = 56;
  const head = 40;
  const gutter = 64;
  const gridTop = y + bar + head;
  const rowH = (y + h - gridTop) / 9;
  const colW = (w - gutter) / 5;
  const colX = (d: number) => x + gutter + d * colW;
  const at = (hr: number) => gridTop + (hr - 9) * rowH;

  let out = panel(x, y, w, h);

  // Toolbar: today, the week, and the three kinds as filters.
  out += box(x + 16, y + 12, 64, 32, 6) + ui(x + 48, y + 32.5, 13, 'Today', { fill: C.text2, weight: 500, anchor: 'middle' });
  out += icons.chevronLeft(x + 104, y + 28) + icons.chevronRight(x + 128, y + 28);
  out += ui(x + 152, y + 33, 14, 'September 14 to 18', { weight: 500, tracking: '-0.011em' });
  let chipRight = x + w - 16;
  for (const kind of ['van', 'person', 'room'] as Kind[]) {
    const o = { dot: KINDS[kind].color, h: 28, end: true };
    out += pill(chipRight, y + 28, KINDS[kind].label, o);
    chipRight -= pillWidth(KINDS[kind].label, o) + 8;
  }
  out += line(x, y + bar, x + w, y + bar, C.hair);

  // Day header, today marked.
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].forEach((day, d) => {
    const lx = colX(d) + 14;
    const cy = y + bar + head / 2;
    out += ui(lx, cy + 4.7, 13, day, { fill: C.text3 });
    const nx = lx + measure(day, 13) + 20;
    if (d === 2) out += circle(nx, cy, 12, C.indigo) + ui(nx, cy + 4.7, 13, String(14 + d), { weight: 600, anchor: 'middle' });
    else out += ui(nx, cy + 4.7, 13, String(14 + d), { fill: C.text2, weight: 500, anchor: 'middle' });
  });
  out += line(x, gridTop, x + w, gridTop, C.hair);

  // Hours and columns.
  for (let hr = 9; hr <= 17; hr += 1) {
    if (hr > 9) out += line(x + gutter, at(hr), x + w, at(hr), 'rgba(255,255,255,0.045)');
    out += ui(x + gutter - 12, at(hr) + 15, 12, `${hr}:00`, { fill: C.text3, anchor: 'end' });
  }
  for (let d = 0; d < 5; d += 1) out += line(colX(d), y + bar, colX(d), y + h, C.hair);

  // Bookings: opaque, a tint of the kind's colour edged in it, and the colour down the left side.
  for (const [d, start, end, kind, title] of WEEK) {
    const ex = colX(d) + 4;
    const ew = colW - 8;
    const ey = at(start) + 1.5;
    const eh = (end - start) * rowH - 3;
    const color = KINDS[kind].color;
    out += rect(ex, ey, ew, eh, C.panel, 6) + rect(ex, ey, ew, eh, tint(color, 0.11), 6) + frame(ex, ey, ew, eh, tint(color, 0.28), 6);
    out += rect(ex + 3.5, ey + 3.5, 2.5, eh - 7, color, 1.25);
    if (eh < 30) {
      out += ui(ex + 14, ey + eh / 2 + 4.3, 12, title, { weight: 500 });
    } else {
      out += ui(ex + 14, ey + 17, 12, title, { weight: 500 });
      out += ui(ex + 14, ey + 33, 12, `${clock(start)} to ${clock(end)}`, { fill: C.text3 });
    }
  }

  // Now, on today.
  const nowY = at(12.4);
  out += line(colX(2), nowY, colX(3), nowY, C.accent, 1.5) + circle(colX(2), nowY, 4, C.accent);
  return out;
}

const bigOne = wrap(
  rect(0, 0, W, H, C.ground) +
    runner(2) +
    headline(2, 'Shared calendars are live.', ['Book a room,', 'a person or a van from one calendar.']) +
    pill(R, HEAD.y - 14, '212 requests', { icon: (cx, cy) => icons.bubble(cx, cy), h: 32, end: true, pad: 12 }) +
    weekCalendar(M, TOP, R - M, BOTTOM - TOP),
);

/* ── 3 · Also shipped ─────────────────────────────────────────────────────────────────────── */

const CARD_W = 368;
const CARD_GAP = 24;
const cardX = (i: number) => M + i * (CARD_W + CARD_GAP);
const CARD_H = BOTTOM - TOP;

const cardTitle = (x: number, y: number, str: string) => ui(x + 24, y + 44, 16, str, { weight: 500, tracking: '-0.011em' });

/** Search: the field, and how long a query took over the cycle. */
function searchCard(x: number, y: number): string {
  let out = panel(x, y, CARD_W, CARD_H) + cardTitle(x, y, 'Faster search');
  out += box(x + 24, y + 68, 320, 48);
  out += icons.search(x + 46, y + 92);
  out += ui(x + 66, y + 97, 15, 'design review', { tracking: '-0.011em' });
  out += rect(x + 66 + measure('design review', 15) + 3, y + 83, 1.5, 18, C.accent);

  // Seconds per query, day by day: flat, then the new index. A faint dotted grid, a thin teal line.
  const values = [1.08, 1.12, 1.06, 1.1, 1.07, 0.46, 0.22, 0.19, 0.2, 0.19];
  const left = x + 40;
  const right = x + 300;
  const top = y + 184;
  const base = y + 404;
  const px = (i: number) => left + (i * (right - left)) / (values.length - 1);
  const py = (v: number) => base - (v / 1.25) * (base - top);
  for (const v of [0.25, 0.5, 0.75, 1, 1.25]) out += line(x + 24, py(v), x + 344, py(v), 'rgba(255,255,255,0.07)', 1, '2 4');
  out += line(x + 24, base, x + 344, base, C.border);
  const pts = values.map((v, i) => `${px(i).toFixed(1)} ${py(v).toFixed(1)}`);
  out += strokePath(`M ${pts.join(' L ')}`, C.teal, 1.75);
  values.forEach((v, i) => {
    if (i < values.length - 1) out += circle(px(i), py(v), 2.25, C.teal);
  });
  out += ui(left, py(values[0]) - 14, 12, '1.1 s', { fill: C.text3, weight: 500 });
  const lastX = px(values.length - 1);
  out += circle(lastX, py(0.19), 8, tint(C.teal, 0.2)) + circle(lastX, py(0.19), 3.5, C.teal);
  out += pill(x + 344, py(0.19) - 32, '190 ms', { fill: C.raised, color: C.text, dot: C.teal, h: 28, end: true });
  return out;
}

/** The app's two themes, as the phone draws them. */
const THEMES = {
  light: { screen: C.text, row: '#E7E9ED', text: C.ground, title: '#2A2C31', meta: '#C3C7CE' },
  dark: { screen: C.ground, row: C.raised3, text: C.text, title: C.text2, meta: C.border2 },
} as const;

/** A phone showing today's bookings in one theme. */
function phone(x: number, y: number, theme: keyof typeof THEMES): string {
  const t = THEMES[theme];
  const w = 124;
  const h = 236;
  let out = rect(x, y, w, h, C.fill, 24) + frame(x, y, w, h, C.border2, 24) + rect(x + 5, y + 5, w - 10, h - 10, t.screen, 19);
  out += rect(x + w / 2 - 16, y + 12, 32, 8, C.fill, 4);
  out += ui(x + 16, y + 48, 13, 'Today', { fill: t.text, weight: 600 });
  [C.accent, C.teal, C.orange, C.accent].forEach((bar, i) => {
    const ry = y + 62 + i * 40;
    out += rect(x + 12, ry, w - 24, 32, t.row, 6) + rect(x + 17, ry + 6, 3, 20, bar, 1.5);
    out += rect(x + 26, ry + 9, 58 - (i % 2) * 14, 6, t.title, 3);
    out += rect(x + 26, ry + 19, 38, 5, t.meta, 2.5);
  });
  return out;
}

/** Dark mode: the same screen both ways, and the switch. */
function darkModeCard(x: number, y: number): string {
  let out = panel(x, y, CARD_W, CARD_H) + cardTitle(x, y, 'Dark mode on Android');
  out += phone(x + 52, y + 84, 'light') + phone(x + 192, y + 84, 'dark');
  out += box(x + 24, y + 364, 320, 52);
  out += ui(x + 44, y + 395, 15, 'Dark mode', { weight: 500, tracking: '-0.011em' });
  out += rect(x + 284, y + 379, 40, 22, C.indigo, 11) + circle(x + 313, y + 390, 9, '#FFFFFF');
  return out;
}

/** Fixes: three of the eleven by name, the other eight as a tally. */
function fixesCard(x: number, y: number): string {
  let out = panel(x, y, CARD_W, CARD_H) + cardTitle(x, y, '11 fixes');
  const fixes = ['Reminders sent twice', 'Wrong time zone in invites', 'Blank week view on Safari'];
  const rowH = 78;
  const gap = 12;
  const rowAt = (i: number) => y + 68 + i * (rowH + gap);
  fixes.forEach((fix, i) => {
    const cy = rowAt(i) + rowH / 2;
    out += box(x + 24, rowAt(i), 320, rowH);
    out += status.done(x + 50, cy, 9) + ui(x + 72, cy + 5.4, 15, fix, { tracking: '-0.011em' });
  });
  const cy = rowAt(3) + rowH / 2;
  out += box(x + 24, rowAt(3), 320, rowH);
  for (let i = 0; i < 8; i += 1) out += status.done(x + 50 + i * 21, cy, 7);
  out += ui(x + 324, cy + 5.4, 15, '+8 more', { fill: C.text3, anchor: 'end' });
  return out;
}

const alsoShipped = wrap(
  rect(0, 0, W, H, C.ground) +
    runner(3) +
    headline(3, 'Three smaller things.') +
    searchCard(cardX(0), TOP) +
    darkModeCard(cardX(1), TOP) +
    fixesCard(cardX(2), TOP),
);

/* ── 4 · What slipped ─────────────────────────────────────────────────────────────────────── */

/** The invoice as it would have gone out: each seat converted and rounded, so the total is a cent short. */
function invoice(x: number, y: number, w: number, h: number): string {
  const pad = 40;
  const right = x + w - pad;
  let out = panel(x, y, w, h);
  out += text(x + pad, y + 58, 22, 'Invoice 0042', { ...headOpts, fill: C.text });
  out += ui(x + pad, y + 86, 14, 'Northwind Studio, billed in US dollars', { fill: C.text3 });
  out += pill(right, y + 51, 'Draft', { icon: (cx, cy) => status.backlog(cx, cy, 6.5), end: true });

  const cols = { qty: x + 330, price: x + 400 };
  const headY = y + 148;
  out += label(x + pad, headY, 'Item');
  out += label(cols.qty, headY, 'Qty', C.text3, 'end');
  out += label(cols.price, headY, 'Price');
  out += label(right, headY, 'Amount', C.text3, 'end');
  out += line(x + pad, headY + 16, right, headY + 16, C.border);

  const rowY = headY + 56;
  out += ui(x + pad, rowY, 16, 'Team seat', { weight: 500, tracking: '-0.011em' });
  out += ui(cols.qty, rowY, 16, '3', { fill: C.text2, anchor: 'end' });
  out += ui(cols.price, rowY, 16, '€9.99', { fill: C.text2 });
  const arrowX = cols.price + measure('€9.99', 16) + 12;
  out += strokePath(`M ${arrowX} ${rowY - 5.5} h 18 M ${arrowX + 13} ${rowY - 10} l 5 4.5 l -5 4.5`, C.text3);
  out += ui(arrowX + 28, rowY, 16, '$10.83', { fill: C.text2 });
  out += ui(right, rowY, 16, '$32.49', { anchor: 'end' });
  out += line(x + pad, rowY + 24, right, rowY + 24, C.hair);

  const labelX = x + 400;
  const euroY = rowY + 74;
  out += ui(labelX, euroY, 15, 'Total in euros', { fill: C.text3 });
  out += ui(right, euroY, 16, '€29.97', { fill: C.text2, anchor: 'end' });

  // The total in dollars, the wrong cent marked.
  const totalY = euroY + 62;
  const size = 34;
  const nine = 0.595 * size;
  out += ui(labelX, totalY - 6, 15, 'Total in dollars', { fill: C.text3 });
  out += rect(right - nine - 3, totalY - 31, nine + 6, 40, C.yellow, 6);
  out += text(right - nine - 3, totalY, size, '$32.4', { ...headOpts, fill: C.text, anchor: 'end', tracking: '0' });
  out += text(right, totalY, size, '9', { ...headOpts, fill: C.ground, anchor: 'end', tracking: '0' });
  out += pill(right, totalY + 42, 'Should be $32.50', { fill: tint(C.yellow, 0.1), stroke: tint(C.yellow, 0.35), color: C.yellow, end: true, size: 14, h: 30 });
  return out;
}

/** The release date, moved: the old one struck through, the new one in the slip's colour. */
function newDate(cx: number, cy: number): string {
  const was = { color: C.text3, h: 52, size: 22, pad: 24 };
  const now = { fill: tint(C.yellow, 0.1), stroke: tint(C.yellow, 0.4), color: C.text, h: 52, size: 22, pad: 22, icon: (x: number, y: number) => status.progress(x, y, 8) };
  const wasW = pillWidth('Sep 21', was);
  const nowW = pillWidth('Oct 5', now);
  return (
    label(cx, cy - 116, 'Planned', C.text3, 'middle') +
    pill(cx - wasW / 2, cy - 78, 'Sep 21', was) +
    line(cx - wasW / 2 + 20, cy - 78, cx + wasW / 2 - 20, cy - 78, C.text3, 1.5) +
    line(cx, cy - 40, cx, cy + 32, C.border2, 1.5, '3 4') +
    strokePath(`M ${cx - 5} ${cy + 27} L ${cx} ${cy + 32} L ${cx + 5} ${cy + 27}`, C.text4) +
    label(cx, cy + 72, 'New date', C.text3, 'middle') +
    pill(cx - nowW / 2, cy + 110, 'Oct 5', now)
  );
}

const INVOICE_W = 720;
const INVOICE_H = CARD_H;

const slipped = wrap(
  rect(0, 0, W, H, C.ground) +
    runner(4) +
    headline(4, 'Invoices move to October 5.', ['We found a rounding bug.', 'Late is better than wrong.']) +
    invoice(M, TOP, INVOICE_W, INVOICE_H) +
    newDate((M + INVOICE_W + R) / 2, TOP + INVOICE_H / 2 - 4),
);

/* ── 5 · How usage moved ──────────────────────────────────────────────────────────────────── */

const USAGE: [number, number][] = [
  [38, 1610],
  [39, 1700],
  [40, 1840],
  [41, 2050],
  [42, 2310],
];

/** Weekly active teams by cycle in a panel: a dotted grid, a thin teal line, the launch marked. */
function usageChart(x: number, y: number, w: number, h: number): string {
  const plotLeft = x + 88;
  const plotRight = x + w - 28;
  const top = y + 76;
  const base = y + h - 68;
  const lo = 1500;
  const hi = 2500;
  // Five cycles, the last one leaving room on its right for the value.
  const px = (i: number) => x + 156 + i * 200;
  const py = (v: number) => base - ((v - lo) / (hi - lo)) * (base - top);
  let out = panel(x, y, w, h);
  for (let v = lo; v <= hi; v += 250) {
    out += v === lo ? line(plotLeft, py(v), plotRight, py(v), C.border) : line(plotLeft, py(v), plotRight, py(v), 'rgba(255,255,255,0.08)', 1, '2 4');
    out += ui(x + 28, py(v) + 4.4, 12, v.toLocaleString('en-US'), { fill: C.text3 });
  }
  USAGE.forEach(([cycle], i) => {
    const last = i === USAGE.length - 1;
    out += ui(px(i), base + 30, 12, `Cycle ${cycle}`, { fill: last ? C.text : C.text3, weight: 500, anchor: 'middle' });
  });

  // The launch.
  const launchX = px(USAGE.length - 1);
  out += line(launchX, top - 10, launchX, base, tint(C.accent, 0.6), 1, '3 4');
  out += pill(launchX, top - 26, 'Shared calendars', { fill: C.raised, dot: C.accent, h: 28 });

  const pts = USAGE.map(([, v], i) => `${px(i)} ${py(v).toFixed(1)}`);
  out += strokePath(`M ${pts.join(' L ')}`, C.teal, 2);
  USAGE.forEach(([, v], i) => {
    if (i < USAGE.length - 1) out += circle(px(i), py(v), 5.5, C.panel) + circle(px(i), py(v), 3.5, C.teal);
  });
  const lastY = py(USAGE[USAGE.length - 1][1]);
  out += circle(launchX, lastY, 11, tint(C.teal, 0.18)) + circle(launchX, lastY, 5, C.teal);
  out += text(launchX + 24, lastY + 11, 32, '2,310', { ...headOpts, fill: C.text });
  out += pill(launchX + 26, lastY + 40, '+260', { fill: tint(C.green, 0.1), stroke: tint(C.green, 0.35), color: C.green, h: 26 });
  return out;
}

const usage = wrap(
  rect(0, 0, W, H, C.ground) +
    runner(5) +
    headline(5, 'More teams, every week.', ['Weekly active teams', 'at the end of each cycle.']) +
    usageChart(M, TOP, R - M, BOTTOM - TOP),
);

/* ── 6 · Next cycle ───────────────────────────────────────────────────────────────────────── */

type Preview = (x: number, y: number, w: number, h: number) => string;

/** The invoice total, right this time. */
const fixedTotal: Preview = (x, y, w, h) => {
  const cy = y + h / 2;
  return (
    box(x + 20, cy - 34, w - 40, 68, 8, C.raised3) +
    ui(x + 40, cy + 5, 14, 'Total in dollars', { fill: C.text3 }) +
    text(x + w - 76, cy + 9, 26, '$32.50', { ...headOpts, fill: C.text, anchor: 'end', tracking: '0' }) +
    status.done(x + w - 52, cy, 10)
  );
};

/** Sharing with someone outside: an address and the button. */
const shareOutside: Preview = (x, y, w, h) => {
  const cy = y + h / 2;
  return (
    box(x + 20, cy - 24, w - 124, 48, 8, C.panel) +
    icons.link(x + 42, cy) +
    ui(x + 58, cy + 5, 14, 'sam@northwind.com', { fill: C.text2 }) +
    rect(x + w - 96, cy - 24, 76, 48, C.indigo, 8) +
    ui(x + w - 58, cy + 5, 14, 'Share', { weight: 500, anchor: 'middle' })
  );
};

/** An iPad on its side with the week on it. */
const ipad: Preview = (x, y, w, h) => {
  const tw = 196;
  const th = 132;
  const tx = x + (w - tw) / 2;
  const ty = y + (h - th) / 2;
  let out = rect(tx, ty, tw, th, C.fill, 14) + frame(tx, ty, tw, th, C.border2, 14) + rect(tx + 7, ty + 7, tw - 14, th - 14, C.ground, 8);
  const colW = (tw - 28) / 4;
  const bookings: [number, number, Kind][] = [
    [0, 0, 'room'], [1, 1, 'person'], [2, 0, 'van'], [3, 2, 'room'], [0, 2, 'person'], [2, 2, 'room'],
  ];
  for (let c = 1; c < 4; c += 1) out += line(tx + 14 + c * colW, ty + 14, tx + 14 + c * colW, ty + th - 14, C.hair);
  for (const [c, r, kind] of bookings) {
    const bx = tx + 16 + c * colW;
    const by = ty + 18 + r * 32;
    out += rect(bx, by, colW - 4, 26, tint(KINDS[kind].color, 0.2), 4) + rect(bx + 2.5, by + 3, 2, 20, KINDS[kind].color, 1);
  }
  return out;
};

const NEXT: { date: string; title: string[]; owner: string; color: string; preview: Preview }[] = [
  { date: 'Oct 5', title: ['Invoices, fixed'], owner: 'Nino', color: C.accent, preview: fixedTotal },
  { date: 'Oct 19', title: ['Share a calendar', 'outside the company'], owner: 'Luka', color: C.teal, preview: shareOutside },
  { date: 'Nov 2', title: ['iPad app beta'], owner: 'Ana', color: C.orange, preview: ipad },
];

/** One roadmap item: what it looks like, what it is, when, and who owns it. */
function roadmapCard(i: number): string {
  const item = NEXT[i];
  const x = cardX(i);
  const y = TOP;
  const inset = 16;
  let out = panel(x, y, CARD_W, CARD_H);
  out += box(x + inset, y + inset, CARD_W - inset * 2, 228, 8) + item.preview(x + inset, y + inset, CARD_W - inset * 2, 228);
  out += lines(x + 24, y + 294, 22, 28, item.title, { ...headOpts, fill: C.text });

  const rowY = y + CARD_H - 40;
  out += line(x, rowY - 28, x + CARD_W, rowY - 28, C.hair);
  out += pill(x + 24, rowY, item.date, { icon: (cx, cy) => icons.calendar(cx, cy), h: 28 });
  const nameW = measure(item.owner, 13);
  const ax = x + CARD_W - 24 - nameW - 8 - 11;
  out += avatar(ax, rowY, 11, item.owner[0], item.color) + ui(ax + 19, rowY + 4.7, 13, item.owner, { fill: C.text3, weight: 500 });
  return out;
}

const next = wrap(
  rect(0, 0, W, H, C.ground) +
    runner(6) +
    headline(6, 'What comes next.') +
    roadmapCard(0) +
    roadmapCard(1) +
    roadmapCard(2),
);

export const UPDATE_SLIDES: DemoSlide[] = [
  { id: 'update-1', title: 'Cover', svg: cover },
  { id: 'update-2', title: 'The big one', svg: bigOne },
  { id: 'update-3', title: 'Also shipped', svg: alsoShipped },
  { id: 'update-4', title: 'What slipped', svg: slipped },
  { id: 'update-5', title: 'How usage moved', svg: usage },
  { id: 'update-6', title: 'Next cycle', svg: next },
];
